import type { Day, LeftoverRef, MealPlanApiItem, MealType, Slot, Task } from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { sync } from "../../sync/SyncService.js";

function nowIso(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * Find a slot by (day date, mealType) POSITION — never by id.
 *
 * `useMealPlan` (G1) can hand the UI a client-synthesized `buildEmptyWeek` fallback when
 * no row has synced down yet. That fallback's slot ids are generated locally and do NOT
 * match the server's POST-created skeleton ids, so editing "the slot with id X" would
 * silently target nothing (or corrupt data) once the real row lands. Position is the only
 * identifier that's stable across the fallback/real-row boundary.
 */
export function findSlot(days: Day[], date: string, mealType: MealType): Slot | undefined {
  return days.find((d) => d.date === date)?.slots.find((s) => s.mealType === mealType);
}

/**
 * Get-or-create the REAL Dexie row for `weekStart` — the single lazy-create step shared by
 * every plan mutation, whether it targets `days` (`editWeek`) or `tasks` (`editWeekTasks`).
 *
 * 1. Reads the REAL Dexie row — never the `useMealPlan` fallback.
 * 2. If it doesn't exist yet (household never created this week), creates it via the
 *    server (`sync.createMealPlanOptimistic`), which lands the real POST-created skeleton
 *    in Dexie. Only after this does a real, addressable row (slot ids, tasks array) exist.
 */
async function getOrCreateWeek(weekStart: string): Promise<MealPlanApiItem> {
  const existing = await db.mealPlans.get(weekStart);
  if (existing) return existing;
  return sync.createMealPlanOptimistic({ weekStart });
}

/**
 * Lazy-create + edit-by-position: the single path every slot mutation goes through.
 *
 * Clones the real row's `days`, hands the clone to `producer` to mutate by position, and
 * PATCHes the whole `days` array back through the optimistic sync path.
 *
 * Because `producer` always operates on the REAL row's `days` (freshly created or
 * pre-existing), any slot id captured from *within* the producer (e.g. a leftover
 * source's `slot.id`) is guaranteed to be a real, server-known id — even if the caller's
 * own rendering was showing the fallback week a moment ago.
 */
export async function editWeek(weekStart: string, producer: (days: Day[]) => Day[]): Promise<void> {
  const existing = await getOrCreateWeek(weekStart);
  const newDays = producer(structuredClone(existing.days));
  await sync.patchMealPlanOptimistic(weekStart, { days: newDays });
}

/**
 * Lazy-create + edit `plan.tasks` — the task-list sibling of `editWeek`. Tasks live on a
 * SEPARATE top-level array from `days`, so this PATCHes `{ tasks }` instead of `{ days }`,
 * but shares the identical get-or-create step via `getOrCreateWeek` (avoids duplicating
 * the lazy-create logic between the two editors).
 */
export async function editWeekTasks(
  weekStart: string,
  producer: (tasks: Task[]) => Task[],
): Promise<void> {
  const existing = await getOrCreateWeek(weekStart);
  const newTasks = producer(structuredClone(existing.tasks));
  await sync.patchMealPlanOptimistic(weekStart, { tasks: newTasks });
}

/**
 * Toggle a task's `done` state by id. Checking stamps `doneBy`/`doneAt` (who/when);
 * unchecking clears both rather than leaving a stale "who/when" on an un-done task.
 */
export async function toggleTaskDone(weekStart: string, taskId: string, userId: string): Promise<void> {
  await editWeekTasks(weekStart, (tasks) =>
    tasks.map((t) => {
      if (t.id !== taskId) return t;
      if (t.done) {
        const cleared = { ...t, done: false };
        delete cleared.doneBy;
        delete cleared.doneAt;
        return cleared;
      }
      return { ...t, done: true, doneBy: userId, doneAt: nowIso() };
    }),
  );
}

/** Append a saved-recipe item into the slot at (date, mealType). */
export async function addRecipeToSlot(
  weekStart: string,
  date: string,
  mealType: MealType,
  recipeId: string,
): Promise<void> {
  await editWeek(weekStart, (days) => {
    const slot = findSlot(days, date, mealType);
    if (slot) slot.items = [...slot.items, { kind: "recipe", recipeId }];
    return days;
  });
}

/** Append a free-text dish into the slot at (date, mealType). */
export async function addFreetextToSlot(
  weekStart: string,
  date: string,
  mealType: MealType,
  title: string,
  reference?: string,
): Promise<void> {
  await editWeek(weekStart, (days) => {
    const slot = findSlot(days, date, mealType);
    if (slot) {
      slot.items = [
        ...slot.items,
        reference !== undefined && reference.length > 0
          ? { kind: "freetext", title, reference }
          : { kind: "freetext", title },
      ];
    }
    return days;
  });
}

/**
 * Mark the slot at (date, mealType) as leftovers from a free-text/prior-week label.
 * (No slot id involved — safe to call with whatever the UI was rendering.)
 */
export async function setSlotLeftover(
  weekStart: string,
  date: string,
  mealType: MealType,
  leftoverOf: LeftoverRef,
): Promise<void> {
  await editWeek(weekStart, (days) => {
    const slot = findSlot(days, date, mealType);
    if (slot) slot.leftoverOf = leftoverOf;
    return days;
  });
}

/**
 * Mark the slot at (date, mealType) as leftovers from another slot IN THIS WEEK,
 * identified by the SOURCE slot's (date, mealType) POSITION — not a captured id.
 *
 * This exists separately from `setSlotLeftover` for a load-bearing reason: the "prior
 * slot in this week" picker in the UI may have been built from the `useMealPlan` fallback
 * (if the week hadn't synced down yet when the sheet opened). Resolving the source slot's
 * id from *that* rendering and threading it through would capture a client-generated
 * fallback id the server has never heard of. By resolving `source.id` from `days` INSIDE
 * the producer — which `editWeek` guarantees is always the REAL (freshly
 * created-or-fetched) row — the id is always addressable.
 */
export async function setSlotLeftoverFromPosition(
  weekStart: string,
  date: string,
  mealType: MealType,
  sourceDate: string,
  sourceMealType: MealType,
): Promise<void> {
  await editWeek(weekStart, (days) => {
    const target = findSlot(days, date, mealType);
    const source = findSlot(days, sourceDate, sourceMealType);
    if (target && source) target.leftoverOf = { slotId: source.id };
    return days;
  });
}

/**
 * Move a slot's full movable payload (items, and — if present — its leftover designation
 * and note) to another slot, appending/filling into the target non-destructively and
 * clearing the source entirely.
 *
 * A leftover-only slot (`items: []` with `leftoverOf` set) is offered "Move" in the sheet
 * because it reads as filled — so Move must relocate `leftoverOf` (and `note`), not just
 * `items`, or the move is silently a no-op for that slot.
 */
export async function moveSlotItems(
  weekStart: string,
  fromDate: string,
  fromMealType: MealType,
  toDate: string,
  toMealType: MealType,
): Promise<void> {
  await editWeek(weekStart, (days) => {
    const source = findSlot(days, fromDate, fromMealType);
    const target = findSlot(days, toDate, toMealType);
    if (source && target) {
      target.items = [...target.items, ...source.items];
      if (source.leftoverOf !== undefined && target.leftoverOf === undefined) {
        target.leftoverOf = source.leftoverOf;
      }
      if (source.note !== undefined && target.note === undefined) {
        target.note = source.note;
      }
      source.items = [];
      delete source.leftoverOf;
      delete source.note;
    }
    return days;
  });
}

/** Destructive: empties the slot's items and clears any leftover marker (back to "+ Add"). */
export async function removeSlotItems(weekStart: string, date: string, mealType: MealType): Promise<void> {
  await editWeek(weekStart, (days) => {
    const slot = findSlot(days, date, mealType);
    if (slot) {
      slot.items = [];
      delete slot.leftoverOf;
    }
    return days;
  });
}

/**
 * Copy the slot at (fromDate, fromMealType)'s `items` into EACH of `targets`, appending
 * non-destructively (never clearing the source — unlike `moveSlotItems`). All resolution
 * (source AND every target) happens by POSITION against the one real `days` clone inside
 * a SINGLE `editWeek` call, so the whole multi-target copy lands as one atomic PATCH —
 * mirrors the edit-by-position discipline documented on `findSlot`/`editWeek` above (never
 * address a slot by an id captured from a `useMealPlan` fallback rendering).
 */
export async function copySlotItemsToTargets(
  weekStart: string,
  fromDate: string,
  fromMealType: MealType,
  targets: { date: string; mealType: MealType }[],
): Promise<void> {
  await editWeek(weekStart, (days) => {
    const source = findSlot(days, fromDate, fromMealType);
    if (!source) return days;
    const itemsToCopy = source.items;
    for (const t of targets) {
      if (t.date === fromDate && t.mealType === fromMealType) continue; // never copy onto itself
      const target = findSlot(days, t.date, t.mealType);
      if (target) target.items = [...target.items, ...itemsToCopy];
    }
    return days;
  });
}

/** Reversible toggle of `slot.skipped` — the skip chip lives on the slot row itself. */
export async function toggleSlotSkipped(weekStart: string, date: string, mealType: MealType): Promise<void> {
  await editWeek(weekStart, (days) => {
    const slot = findSlot(days, date, mealType);
    if (slot) slot.skipped = slot.skipped !== true;
    return days;
  });
}
